/// <reference path="../../../../Frontend.Core/dist/definitions/NCB.d.ts" />
/// <reference path="../../../../Frontend.Core/dist/definitions/NCB.DateUtils.d.ts" />
/// <reference path="../../../../Frontend.Core/dist/definitions/NCB.Calendar.d.ts" />
/// <reference path="../../../../Frontend.Core/dist/definitions/NCB.I18n.d.ts" />

import * as React from "react";
import {observer} from 'mobx-react';
import appstate from "../../appstate";
import calendarStore from "../../stores/calendarStore";
import {IEndDateStatus} from "../../stores/calendarStore";
import {CurrencySelect} from "../shared/currencySelect";

export interface IPriceCalendarProps {
    selectedStartDate: Date,
    selectedEndDate: Date,
    setDatesDateCallback: any,
    firstSelectableDate: Date,
    startMonthIndex: number,
    promotionCode: string
}

export interface IPriceCalendarState {
    selectedStartDate: string,
    selectedEndDate: string,
    monthIndex: number,
    hoverDate: string
}

export interface ICalendarMonth {
    days: JSX.Element[]
}

@observer
export class PriceCalendar extends React.Component<IPriceCalendarProps, IPriceCalendarState> {

    doSelectStartDate: boolean = true; // Toggle between selecting the end date and the start date

    private calendarResponse: CC.Library.Models.Calendar.DetailedDurationCalendarResponse = null;

    private paginateIcon = <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="34.266px" height="71.329px" viewBox="0 0 34.266 71.329" enable-background="new 0 0 34.266 71.329" xmlSpace="preserve">
            <polyline fill="none" stroke-width="2" stroke-miterlimit="10" points="8.946,7.684 26.99,35.19 8.925,62.73 "/>
        </svg>;

    constructor(props: IPriceCalendarProps) {
        super(props);

        this.props = props;

        this.handleClick = this.handleClick.bind(this);
        this.monthPagePrev = this.monthPagePrev.bind(this);
        this.monthPageNext = this.monthPageNext.bind(this);
        this.handleDayHover = this.handleDayHover.bind(this);
        this.isEndDateAvailable = this.isEndDateAvailable.bind(this);

        this.state = {
            selectedStartDate: NCB.DateUtils.getISODate(this.props.selectedStartDate),
            selectedEndDate: NCB.DateUtils.getISODate(this.props.selectedEndDate),
            monthIndex: props.startMonthIndex,
            hoverDate: null
        };
    }

    componentDidMount() {
        calendarStore.loadCallback = () => { this.forceUpdate(); };
    }

    componentWillUnmount() {
        calendarStore.loadCallback = null;
    }

    componentWillReceiveProps(nextProps: IPriceCalendarProps) {
        if (nextProps.selectedStartDate !== this.props.selectedStartDate || nextProps.selectedEndDate !== this.props.selectedEndDate) {

            this.setState({
                selectedStartDate: NCB.DateUtils.getISODate(nextProps.selectedStartDate),
                selectedEndDate: NCB.DateUtils.getISODate(nextProps.selectedEndDate)                
            })
        }   
    }
    
    getStatusName(status: CC.Library.Models.Calendar.AvailabilityStatus) {
        switch (status) {
            case CC.Library.Models.Calendar.AvailabilityStatus.Closed: return "close";
            case CC.Library.Models.Calendar.AvailabilityStatus.Open: return "open";
            case CC.Library.Models.Calendar.AvailabilityStatus.ClosedOnArrival: return "closedOnArrival";
            case CC.Library.Models.Calendar.AvailabilityStatus.MinStay: return "minStay";
            case CC.Library.Models.Calendar.AvailabilityStatus.MaxStay: return "maxStay";
        }
    }

    handleClick(e: any) {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        let dateString = e.currentTarget.getAttribute("data-date");
        let newDate = new Date(Date.parse(dateString));

        if (this.doSelectStartDate) {
            this.setState({
                selectedStartDate: dateString,
                selectedEndDate: null,
                hoverDate: null
            });
        } else {
            // Are we trying to select the same date as the start
            if (dateString === this.state.selectedStartDate)
                return;

            this.setState({
                selectedEndDate: dateString,
                hoverDate: null
            });
            
            this.props.setDatesDateCallback(new Date(Date.parse(this.state.selectedStartDate)), newDate, this.state.monthIndex);
        }

        this.doSelectStartDate = !this.doSelectStartDate;
    }

    loadAvailability = () => {
        let currentMonthIndex = -1;
        let lastMonth: string = null;
        calendarStore.calendarResponse.Availability.forEach((x, i) => {
            let parsedMonth = x.Date.split('-')[1];
            if (lastMonth == null || lastMonth != parsedMonth) {
                currentMonthIndex++;
                lastMonth = parsedMonth;
                if (currentMonthIndex == this.state.monthIndex) {
                    calendarStore.hintStartDate = new Date(Date.parse(calendarStore.calendarResponse.Availability[i].Date));
                }
            } 
        });
    }

    monthPagePrev() {
        let newIndex = this.state.monthIndex - 1;
        if (newIndex < 0)
            newIndex = 0;

        this.setState({
            monthIndex: newIndex
        }, this.loadAvailability);
    }
    monthPageNext() {
        let newIndex = this.state.monthIndex + 1;

        this.setState({
            monthIndex: newIndex
        }, this.loadAvailability);        
    }

    handleDayHover(e: any) {
        this.setState({
            hoverDate: e.currentTarget.getAttribute("data-date")
        })
    }

    isEndDateAvailable(checkEndDate: string): IEndDateStatus {
        if (this.state.selectedStartDate == null)
            return IEndDateStatus.Unavailable
        

        return calendarStore.isEndDateAvailable(new Date(Date.parse(this.state.selectedStartDate)), new Date(Date.parse(checkEndDate)));
    }

    /* STYLE */
    private style: React.CSSProperties = {
        position: "relative"
    }

    private dayStyle: React.CSSProperties = {
        float: "left",
        width: "14.28%"
    }
    private weekdayLabelStyle: React.CSSProperties = {
        clear: "both"
    }

    render() {
        let monthCounter = 0;
        let dayIndex: number = null;
        let monthLabel: string =  null;
        let showNumDays = 35;

        let days: JSX.Element[] = [];

        let firstDate = new Date(calendarStore.calendarResponse.Availability[0].Date);
        let firstDayIndex = NCB.DateUtils.getDay(firstDate);
        
        let infoLabel: string = null;

        // Does the first available month start on a day except monday? In that case, fill up with days from previous month
        if (firstDayIndex !== 0) {
            let prefillDays = firstDayIndex;

            while (prefillDays > 0) {
                prefillDays--;
                
                let tempDate = new Date(firstDate.getTime());
                tempDate.setUTCDate(prefillDays * -1);
                
                days.push(<div className="ncb-priceCalendar-day ncb-priceCalendar-day-status-prefill" style={this.dayStyle}>{tempDate.getUTCDate()}</div>);   
            }
        }
        
        let currentMonth = new Date(Date.parse(calendarStore.calendarResponse.Availability[0].Date)).getUTCMonth();

        // Iterate over the available dates from the calendar and build the HTML
        let selectedStartDay: CC.Library.Models.Calendar.CalendarDay;
        let isPassedToday = false;
        let isPassedStartDate = false;
        let isPassedEndDate = false;
        let isStayDate = false;

        let hoverHandler: any = null;
        let doHighlight: boolean = false;
        let prevDay: CC.Library.Models.Calendar.CalendarDay;
        let nightsSinceStartDay = -1;

        let minMaxMessage: JSX.Element = null;

        // MAIN LOOP
        calendarStore.calendarResponse.Availability.forEach((x) => {

            if (nightsSinceStartDay > -1)
                nightsSinceStartDay++;
            
            let dateIsRestricted = false;

            let parsedDate = new Date(Date.parse(x.Date));

            let classes: string[] = ["ncb-priceCalendar-day", "ncb-priceCalendar-day-status-" + this.getStatusName(x.Status)];

            let monthCheck = parsedDate.getUTCMonth();
            if (monthCheck != currentMonth) {
                currentMonth = monthCheck;
                monthCounter++;
            }         

            if (monthCounter === this.state.monthIndex) {
                classes.push("ncb-is-current-month");
                if (monthLabel === null) {
                    monthLabel = NCB.DateUtils.getMonthName(parsedDate.getUTCMonth()) + " " + parsedDate.getUTCFullYear();

                    // How many rows (weeks) do we need to show for this month?
                    let numDaysInMonth = new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth() + 1, 0).getDate();
                    let startDayIndex = parsedDate.getUTCDay();
                    if (startDayIndex === 0)
                        startDayIndex = 6;
                    else 
                        startDayIndex--;

                    if (numDaysInMonth + startDayIndex > 35)
                        showNumDays = 42;
                }
            }

            if (dayIndex === null && monthCounter === this.state.monthIndex) {
                dayIndex = days.length;
            }

            if (this.state.selectedStartDate === x.Date) {
                selectedStartDay = x;
                isPassedStartDate = true;
                infoLabel = NCB.I18n.T.CHECK_IN;
                nightsSinceStartDay = 0;

                if (x.Status === CC.Library.Models.Calendar.AvailabilityStatus.MinStay) {
                    minMaxMessage = <div className="ncb-minMaxStayMessage">{NCB.I18n.T.MINIMUM} {selectedStartDay.Quantity} {NCB.I18n.T.NIGHTS_OF_STAY_FOR_ARRIVAL_ON_THIS_DATE}</div>;
                } else if (x.Status === CC.Library.Models.Calendar.AvailabilityStatus.MaxStay) {
                    minMaxMessage = <div className="ncb-minMaxStayMessage">{NCB.I18n.T.MAXIMUM} {selectedStartDay.Quantity} {NCB.I18n.T.NIGHTS_OF_STAY_FOR_ARRIVAL_ON_THIS_DATE}</div>;
                }
                
                if (!this.doSelectStartDate) {
                    hoverHandler = this.handleDayHover;

                    doHighlight = this.state.hoverDate != null;
                }
            } else if (this.state.selectedEndDate === x.Date) {
                isPassedEndDate = true;
                isStayDate = false;
                infoLabel =  NCB.I18n.T.CHECK_OUT;
            } else {
                infoLabel = null;

                // Between start date and end date?
                isStayDate = this.state.selectedEndDate != null && isPassedStartDate && !isPassedEndDate;

                if (doHighlight && this.state.hoverDate === x.Date)
                    infoLabel =  NCB.I18n.T.CHECK_OUT;
            } 

            if (isPassedToday === false) {
                if (parsedDate >= this.props.firstSelectableDate) {
                    classes.push("ncb-priceCalendar-day-first-selectable");
                    isPassedToday = true;
                } else {
                    classes = ["ncb-priceCalendar-day"];
                }
            } 
            
            // Add Clickhandler?
            let clickHandler = null;
            if (isPassedToday) {
                
                if (this.doSelectStartDate && x.Status !== CC.Library.Models.Calendar.AvailabilityStatus.Closed && x.Status !== CC.Library.Models.Calendar.AvailabilityStatus.ClosedOnArrival) {
                    classes.push("selectable");
                    clickHandler = this.handleClick;
                }

                if (!this.doSelectStartDate && isPassedStartDate) {
                    let endDateStatus = this.isEndDateAvailable(x.Date);
                    if (endDateStatus === IEndDateStatus.Available) {
                        classes.push("selectable");
                        clickHandler = this.handleClick;
                    } else if (endDateStatus === IEndDateStatus.Loading) {
                        classes.push("ncb-availability-pending");
                    } else {
                        // This checkout date has no availability
                        if (this.state.selectedStartDate !== x.Date) {
                            dateIsRestricted = true;
                        }
                    }
                }
            }

            // NCB-594 Add the possibility to select a new start date while selecting end date
            if (clickHandler == null && !this.doSelectStartDate && !isPassedStartDate) {
                if (x.Status !== CC.Library.Models.Calendar.AvailabilityStatus.Closed && x.Status !== CC.Library.Models.Calendar.AvailabilityStatus.ClosedOnArrival) {
                    clickHandler = (e: any) => {
                        this.doSelectStartDate = true;
                        this.handleClick(e);
                    }
                }
            }

            // Handle Min/Max stay 
            if (!this.doSelectStartDate && isPassedStartDate) {
                // MinStay
                if (selectedStartDay.Status === CC.Library.Models.Calendar.AvailabilityStatus.MinStay) {
                    if (nightsSinceStartDay < parseInt(selectedStartDay.Quantity)) {
                        clickHandler = () => { alert(NCB.I18n.T.PRICE_CALENDAR_MIN_NIGHTS_REQUIRED.replace('%QTY%', selectedStartDay.Quantity)) };
                    }
                }

                // MaxStay
                if (selectedStartDay.Status === CC.Library.Models.Calendar.AvailabilityStatus.MaxStay) {
                    if (nightsSinceStartDay > parseInt(selectedStartDay.Quantity)) {
                        clickHandler = () => { alert(NCB.I18n.T.PRICE_CALENDAR_MAX_NIGHTS_AVAILABLE.replace('%QTY%', selectedStartDay.Quantity)) };
                        doHighlight = false;
                        infoLabel = "";
                    }
                }
            }

            let shortPrice: string = "";
            if (x.AmountAfterTax !== '-1') {
                shortPrice = (isPassedToday ? NCB.Utils.formatPrice(NCB.CurrencyHelper.getConvertedPrice(NCB.Settings.Currency, parseFloat(x.AmountAfterTax), appstate.currency)) : null);
            }

            let shortPriceCurrency:JSX.Element = (isPassedToday ? <span className="ncb-currency">{appstate.currency}</span>: null);
            days.push(<div className={classes.join(" ")}  
                           style={this.dayStyle} 
                           onClick={clickHandler} 
                           onMouseEnter={hoverHandler}
                           data-date={x.Date} 
                           data-is-staydate={isStayDate} 
                           data-is-startdate={(this.state.selectedStartDate === x.Date)} 
                           data-is-enddate={(this.state.selectedEndDate === x.Date)}
                           data-is-highlighted={doHighlight}
                           data-is-restricted={dateIsRestricted}
                           data-num-nights={nightsSinceStartDay}>
                <span className="ncb-dateLabel">{parsedDate.getUTCDate()}</span>
                <span className="ncb-day-price">{shortPrice} {shortPriceCurrency}</span>
                <span className="ncb-day-info">{infoLabel}</span>
            </div>);

            if (x.Date === this.state.hoverDate)
                doHighlight = false;
            
            prevDay = x;
        });

        let nextButton = monthCounter > this.state.monthIndex ? <span className="ncb-priceCalendar-month-paginator-next-icon" onClick={this.monthPageNext}>{this.paginateIcon}</span> : null;
        let prevButton = this.state.monthIndex === 0 ? null : <span className="ncb-priceCalendar-month-paginator-prev-icon"  onClick={this.monthPagePrev}>{this.paginateIcon}</span>;

        // Splice out the selected month from the days array
        if (dayIndex < 7) {
            dayIndex = 0;
        } else {
            dayIndex = dayIndex - (dayIndex % 7);
        }

        days = days.slice(dayIndex, dayIndex + showNumDays);

        // Currency selector
        let currencySelect: JSX.Element = null;
        if (NCB.Settings.FeatureMatrixConfiguration.Currencies.length > 1)
            currencySelect = <div className="ncb-currencySelect-wrapper"><CurrencySelect selectedCurrency={appstate.currency} onSelect={appstate.selectCurrency} /></div>

        return <div className="ncb-priceCalendar" style={this.style} onClick={(e: any) => {e.stopPropagation()}}>
            <div className="ncb-priceCalendar-inner">
                <div className="ncb-priceCalendar-month-paginator" style={{position: "relative"}}>
                    {prevButton}
                    {nextButton}

                    <div className="ncb-priceCalendar-month-label">{monthLabel}</div>
                    
                    {this.props.children}
                </div>
                <div className="ncb-priceCalendar-days">
                    <div className="ncb-priceCalendar-weekdays-label" style={this.weekdayLabelStyle}>
                        <div className="ncb-priceCalendar-weekdays-label-day" style={this.dayStyle}>{NCB.I18n.T.WEEKDAY_0.substr(0,2)}</div>
                        <div className="ncb-priceCalendar-weekdays-label-day" style={this.dayStyle}>{NCB.I18n.T.WEEKDAY_1.substr(0,2)}</div>
                        <div className="ncb-priceCalendar-weekdays-label-day" style={this.dayStyle}>{NCB.I18n.T.WEEKDAY_2.substr(0,2)}</div>
                        <div className="ncb-priceCalendar-weekdays-label-day" style={this.dayStyle}>{NCB.I18n.T.WEEKDAY_3.substr(0,2)}</div>
                        <div className="ncb-priceCalendar-weekdays-label-day" style={this.dayStyle}>{NCB.I18n.T.WEEKDAY_4.substr(0,2)}</div>
                        <div className="ncb-priceCalendar-weekdays-label-day" style={this.dayStyle}>{NCB.I18n.T.WEEKDAY_5.substr(0,2)}</div>
                        <div className="ncb-priceCalendar-weekdays-label-day" style={this.dayStyle}>{NCB.I18n.T.WEEKDAY_6.substr(0,2)}</div>
                    </div>
                    <div className="ncb-priceCalendar-days-list">
                        {days}
                    </div>
                </div>

                {minMaxMessage}
            </div>

            {currencySelect}
        </div>
    }
}




// WEBPACK FOOTER //
// ./src/components/datepicker/priceCalendar.tsx